<script setup lang="ts">
import ContactCard from '@/components/ContactCard.vue';
import useLoading from '@/composables/useLoading';
import withRetry from '@/composables/withRetry';
import { useAppApi, type ContractorContactDto, type SamplingContactDto } from '@/plugins/appApi';
import { useNetworkStore } from '@/stores/network';
import { useProjectStore } from '@/stores/project';
import { onMounted, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
const router = useRouter();
const client = useAppApi();
const projectStore = useProjectStore();

const contacts: Ref<ContractorContactDto[]> = ref([]);
const samplingContacts: Ref<SamplingContactDto[]> = ref([]);

const loading = ref(false);

onMounted(() =>
  useLoading(loading, async () => {
    try {
      const res = await withRetry(() =>
        client.GET('/api/projects/{uid}/contacts', {
          params: {
            path: {
              uid: projectStore.getProjectUid()
            }
          }
        })
      );
      contacts.value = res.data!;

      const res2 = await withRetry(() => client.GET('/api/sampling-contacts'));
      samplingContacts.value = res2.data!;
    } catch (e) {
      useNetworkStore().signalIssue(e);
    }
  })
);
</script>

<template>
  <div class="flex flex-col pb-12">
    <div class="flex items-center gap-2 mb-6">
      <Button
        icon="pi pi-angle-left"
        size="small"
        rounded
        aria-label="arrow right"
        @click="router.back()"
      />
    </div>
    <h1 class="text-2xl min-[1300px]:text-5xl text-lumar-blue font-bold mb-14">
      {{ $t('contacts.title') }}
    </h1>
    <div
      class="flex flex-col gap-4 min-[1300px]:gap-11 pb-6 min-[1300px]:pb-14 border-b border-lumar-medium-gray"
    >
      <h2 class="text-xl min-[1300px]:text-4xl text-lumar-blue">
        {{ $t('contacts.lumarContacts') }}
      </h2>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <div
        class="flex flex-col gap-1.5 min-[1300px]:grid min-[1300px]:grid-cols-4 min-[1300px]:gap-5"
      >
        <ContactCard
          v-for="contact in contacts"
          :key="contact.id"
          :phone="contact.phone"
          :name="contact.name"
          :role="$t('contacts.roles.' + contact.role)"
          :email="contact.email"
          :id="contact.id"
        />
      </div>
    </div>
    <div class="flex flex-col gap-4 min-[1300px]:gap-11 pb-6 mt-6 min-[1300px]:mt-14">
      <h2 class="text-xl min-[1300px]:text-4xl text-lumar-blue">
        {{ $t('contacts.samplingContacts') }}
      </h2>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <Skeleton
        height="64px"
        v-if="loading"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-gray-10)',
              'margin-bottom': '0'
            }
          }
        }"
      ></Skeleton>
      <div
        class="flex flex-col gap-1.5 min-[1300px]:grid min-[1300px]:grid-cols-4 min-[1300px]:gap-5"
      >
        <ContactCard
          v-for="contact in samplingContacts"
          :key="contact.email ?? ''"
          :phone="contact.phone ?? ''"
          :name="contact.title ?? ''"
          :role="contact.category ?? ''"
          :email="contact.email ?? ''"
        />
      </div>
    </div>
  </div>
</template>
